import { createTheme, type Theme } from '@consumer/ui'
import { extendTheme, themeTools } from 'native-base'
import type { InterfaceTagProps } from 'native-base/lib/typescript/components/composites/Tag/types'

// CWA-only color overrides that cannot yet collapse into the unified tokens.
// primary/info/success/error/warning/gray now match tokens.ts (ratified CBP-3947)
// and have been removed. Remaining entries cover:
//   • brand collisions still pending migration (lightBlue, darkGrey, softBlack)
//   • softBlack scale (migration target → brand.textSoftBlack)
//   • vermillion scale with the Figma-canonical #8E2A1A shade (ratified CBP-3947)
//   • success.1000 (non-standard key kept until CWA migrates to success.500)
// All entries are enumerated in packages/ui/src/theme/DECISIONS.md.
const legacyColors = {
  brand: {
    white: '#FFFFFF',
    warmWhite: '#F5F5ED',
    black: '#000000',
    softBlack: '#21211F',
    transparent: 'transparent',
    text: '#000000',
    vermillion: '#E84931',
    darkVermillion: '#8B2C1D',
    lightGrey: '#ECECE5',
    darkGrey: '#858581',
    selectedBlue: '#A7CEEB',
    selectedDarkBlue: '#3B5466',
    lightBlue: '#DCEBF7',
    marigold: '#F8E6BB',
    darkMarigold: '#6C4D05',
    successDarkGreen: '#22562E',
  },
  // Non-standard key kept for CWA compatibility until callers migrate to
  // success.500 (#21BA43). Cannot be an additive key without a deep-merge
  // adapter — the full scale must be repeated to avoid clobbering tokens.
  success: {
    50: '#D4F3D9',
    100: '#CBFAD6',
    200: '#D4F3D9',
    300: '#71E28A',
    400: '#21BA43',
    500: '#21BA43',
    600: '#21BA43',
    700: '#21BA43',
    800: '#298137',
    900: '#298137',
    1000: '#2D5A37',
  },
  softBlack: {
    50: '#21211F',
    100: '#21211F',
    200: '#21211F',
    300: '#21211F',
    400: '#21211F',
  },
  vermillion: {
    100: '#F6B6AD',
    300: '#E84931',
    500: '#8E2A1A',
  },
}

// Unified theme tokens (CBP-3939, ratified CBP-3947): primary is now
// vermillion in tokens.ts; all palette scales match CWA. Only the small
// set of CWA-specific overrides in legacyColors remains.
export const appTheme: Theme = createTheme()

export const colors = { ...appTheme.colors, ...legacyColors }

const components = {
  Alert: {
    baseStyle: {
      borderRadius: 8,
    },
  },
  Container: {
    baseStyle: (props: unknown) => {
      return {
        color: themeTools.mode(colors.brand.black, colors.brand.white)(props),
        p: '6',
        w: [868, '100%', '100%', 868, 868],
        maxWidth: null,
      }
    },
  },
  Heading: {
    baseStyle: (props: unknown) => {
      return {
        color: themeTools.mode(colors.brand.black, colors.brand.white)(props),
        backgroundColor: themeTools.mode(colors.brand.white, colors.brand.black)(props),
        fontFamily: 'heading',
      }
    },
    sizes: {
      xs: {
        fontSize: '14px',
      },
      sm: {
        fontSize: '16px',
      },
      md: {
        fontSize: '20px',
      },
      lg: {
        fontSize: '24px',
      },
      xl: {
        fontSize: '30px',
      },
      '2xl': {
        fontSize: '36px',
      },
      '3xl': {
        fontSize: '48px',
      },
      '4xl': {
        fontSize: '60px',
      },
    },
  },
  FormControl: {
    baseStyle: (props: unknown) => {
      return {
        color: themeTools.mode(colors.brand.black, colors.brand.white)(props),
        backgroundColor: themeTools.mode(colors.brand.white, colors.brand.black)(props),
      }
    },
  },
  FormControlLabel: {
    baseStyle: (props: unknown) => {
      return {
        _text: {
          color: themeTools.mode('gray.600', 'gray.200')(props),
          fontWeight: '600',
          fontSize: '12px',
          fontFamily: 'Matter Regular',
        },
      }
    },
    defaultProps: {
      _text: {},
    },
  },
  FormControlHelperText: {
    baseStyle: (props: unknown) => {
      return {
        _text: {
          fontFamily: 'Matter Regular',
          color: themeTools.mode('gray.500', 'gray.200')(props),
          fontWeight: '400', //changed as per the spec
          fontSize: '14px', //changed as per the spec
          lineHeight: '150%',
        },
      }
    },
    defaultProps: {
      _text: {},
    },
  },
  Input: {
    baseStyle: (props: unknown) => {
      return {
        fontFamily: 'Matter Regular',
        px: '14px',
        py: '12px',
        borderColor: colors.brand.softBlack,
        _focus: {
          borderWidth: 2,
          borderColor: colors.brand.softBlack,
          backgroundColor: themeTools.mode(colors.brand.white, colors.brand.black)(props),
          _invalid: {
            borderWidth: 2,
          },
          placeholderTextColor: colors.brand.softBlack,
          _focus: {
            borderColor: colors.brand.softBlack,
            borderWidth: 2,
          },
          // NativeBase 3.4.28 input.ts:83 sets _focus._stack.style.boxShadow to
          // `0 0 0 1px ${primary[600]}` which renders a #9E1600 ring now that
          // the legacy #f5f5ed warmWhite override has been removed. Suppressing
          // it here preserves CWA's zero-ring focused-input style.
          _stack: {
            style: {
              boxShadow: 'none',
            },
          },
        },
        _hover: {
          borderWidth: 2,
          borderColor: colors.brand.softBlack,
          backgroundColor: themeTools.mode(colors.brand.white, colors.brand.black)(props),
          _invalid: {
            borderWidth: 2,
          },
          placeholderTextColor: colors.brand.softBlack,
          _hover: {
            borderColor: colors.brand.softBlack,
            borderWidth: 2,
          },
        },
      }
    },
    defaultProps: () => {
      return {
        size: 'md',
        placeholderTextColor: 'gray.300',
        borderWidth: 2,
        borderColor: colors.brand.softBlack,
        // focusOutlineColor: 'transparent', // Fix webkit bug https://bugs.webkit.org/show_bug.cgi?id=20807 not fixed on Safari Version 16.0 (17614.1.25.9.10, 17614)
        // inValidOutlineColor: 'transparent', // Fix webkit bug https://bugs.webkit.org/show_bug.cgi?id=20807 not fixed on Safari Version 16.0 (17614.1.25.9.10, 17614)
      }
    },
  },
  Checkbox: {
    baseStyle: (props: unknown) => {
      return {
        borderWidth: '1px',
        borderRadius: '2px',
        borderColor: 'gray.400',
        padding: '3px',
        _checked: {
          borderWidth: '1px',
          borderColor: 'gray.400',
          backgroundColor: themeTools.mode('primary.300', colors.brand.white)(props),
        },
        _text: {
          fontFamily: 'Matter Regular',
          lineHeight: '17.5px',
          ml: 0,
        },
        _stack: {
          alignItems: 'baseline',
        },
      }
    },
    defaultProps: {
      _icon: { size: 2 },
      _text: {
        fontSize: '14px',
      },
    },
  },
  Text: {
    defaultProps: {
      size: 'body',
    },
    sizes: {
      label: {
        fontSize: '10px',
      },
      caption: {
        fontSize: '12px',
      },
      smallBody: {
        fontSize: '14px',
      },
      body: {
        fontSize: '16px',
      },
      display: {
        fontSize: '20px',
      },
      displayLarge: {
        fontSize: '24px',
      },
      displayXL: {
        fontSize: '32px',
      },
      display2XL: {
        fontSize: '40px',
      },
    },
    variants: {
      pressable: {
        fontSize: 16,
        fontWeight: '500',
        lineHeight: 16,
      },
    },
  },
  Tag: {
    baseStyle: (props: InterfaceTagProps & { theme: unknown }) => {
      const isPrimary = props.colorScheme === 'primary'
      return {
        _text: {
          _light: {
            color: isPrimary ? `${props.colorScheme}.300` : `${props.colorScheme}.500`,
          },
          fontWeight: '600',
        },
      }
    },
    sizes: {
      sm: {
        py: '0.8',
        _text: {
          fontSize: '2xs',
        },
      },
      md: {
        py: '1.5',
      },
      lg: {
        py: 2,
      },
    },
  },
  SectionList: {
    baseStyle: (props: unknown) => {
      return {
        color: themeTools.mode(colors.brand.black, colors.brand.white)(props),
        backgroundColor: themeTools.mode(colors.brand.white, colors.brand.black)(props),
      }
    },
  },
  Select: {
    baseStyle: {
      borderWidth: 2,
      borderColor: 'brand.softBlack',
      bg: 'brand.white',
    },
  },
  Slider: {
    defaultProps: {
      colorScheme: 'secondary',
    },
  },
  Spinner: {
    baseStyle: {
      color: 'secondary.300',
    },
  },
  Modal: {
    baseStyle: {
      position: 'sticky',
      top: 0,
      height: '100vh',
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
    },
  },
  Tooltip: {
    baseStyle: () => {
      return {
        _text: {
          textAlign: 'center',
          fontFamily: 'Matter Regular',
          fontSize: '12px',
          fontWeight: '400',
          color: colors.brand.white,
          px: 3,
        },
        bg: 'info.300',
      }
    },
    defaultProps: {
      bg: 'info.300',
    },
  },
  ModalHeader: {
    baseStyle: () => {
      return {
        px: '3',
        pt: '3',
        pb: '1',
        borderBottomWidth: '0',
        _text: {
          color: 'text.900',
          fontSize: 'md',
          fontWeight: 'semibold',
          lineHeight: 'sm',
        },
        bg: 'muted.50',
        borderColor: 'muted.800',
        _dark: {
          bg: 'muted.800',
          borderColor: 'muted.700',
          _text: {
            color: 'text.50',
          },
        },
      }
    },
  },
  Button: {
    baseStyle: {
      _disabled: {
        opacity: 20,
      },
    },
    sizes: {
      lg: {
        px: '24px',
        py: '12px',
        _text: {
          fontSize: 'sm',
        },
      },
      sm: {
        px: '16px',
        py: '4px',
        _text: {
          fontSize: 'xs',
        },
      },
    },
    variants: {
      rounded: () => {
        return {
          bg: colors.primary[300],
          _text: {
            color: colors.brand.white,
            fontWeight: 600,
            fontFamily: 'Matter Regular',
          },
          _pressed: {
            bg: colors.primary[500],
          },
          rounded: 'full',
        }
      },
      roundedSubtle: () => {
        return {
          bg: colors.info[100],
          _text: {
            color: colors.brand.black,
            fontWeight: 600,
            fontFamily: 'Matter Regular',
          },
          _pressed: {
            bg: colors.primary[100],
          },
          rounded: 'full',
        }
      },
      instructions: () => {
        return {
          size: 'sm',
          width: 'fit-content',
          height: '32px',
          borderRadius: 100,
          bgColor: colors.brand.softBlack,
          _text: { color: colors.brand.warmWhite, fontWeight: 600 },
        }
      },
    },
    defaultProps: {
      variant: 'rounded',
      size: 'lg',
    },
  },
}

const fontSizes = {
  '2xs': 10, // not part of official theme, but used in designs
  xs: 14,
  sm: 16,
  md: 20, // Display
  lg: 24,
  xl: 30,
  '2xl': 36,
  '3xl': 48,
  '4xl': 60,
  '5xl': 60, // not part of official theme
  '6xl': 60, // not part of official theme
  '7xl': 60, // not part of official theme
  '8xl': 60, // not part of official theme
  '9xl': 60, // not part of official theme
}

const fontConfig = {
  Matter: {
    100: {
      normal: 'Matter-Light',
      italic: 'Matter-LightItalic',
    },
    200: {
      normal: 'Matter-Light',
      italic: 'Matter-LightItalic',
    },
    300: {
      normal: 'Matter-Light',
      italic: 'Matter-LightItalic',
    },
    400: {
      normal: 'Matter-Regular',
      // Fixed: CWA previously referenced 'Matter-Italic' which does not exist
      // on disk. The actual file in both apps/web/src/fonts/ and
      // apps/mobile/assets/fonts/ is Matter-RegularItalic.ttf. CMA was
      // already correct. Bug documented in packages/ui/src/theme/DECISIONS.md.
      italic: 'Matter-RegularItalic',
    },
    500: {
      normal: 'Matter-Medium',
      italic: 'Matter-MediumItalic',
    },
    600: {
      normal: 'Matter-SemiBold',
      italic: 'Matter-SemiBoldItalic',
    },
    700: {
      normal: 'Matter-Bold',
      italic: 'Matter-BoldItalic',
    },
    800: {
      normal: 'Matter-Heavy',
      italic: 'Matter-HeavyItalic',
    },
    900: {
      normal: 'Matter-Heavy',
      italic: 'Matter-HeavyItalic',
    },
  },
  Newsreader: {
    100: {
      normal: 'Newsreader-Regular',
      italic: 'Newsreader-Italic',
    },
    200: {
      normal: 'Newsreader-Regular',
      italic: 'Newsreader-Italic',
    },
    300: {
      normal: 'Newsreader-Regular',
      italic: 'Newsreader-Italic',
    },
    400: {
      normal: 'Newsreader-Regular',
      italic: 'Newsreader-Italic',
    },
    500: {
      normal: 'Newsreader-Regular',
      italic: 'Newsreader-Italic',
    },
    600: {
      normal: 'Newsreader-SemiBold',
      italic: 'Newsreader-SemiBoldItalic',
    },
    700: {
      normal: 'Newsreader-Bold',
      italic: 'Newsreader-BoldItalic',
    },
    800: {
      normal: 'Newsreader-Bold',
      italic: 'Newsreader-BoldItalic',
    },
    900: {
      normal: 'Newsreader-Bold',
      italic: 'Newsreader-BoldItalic',
    },
  },
}

export const fonts = {
  headingRebrand: 'Newsreader Regular',
  heading: 'Matter Regular',
  body: 'Matter Regular',
  bodySemiBold: 'Matter SemiBold',
  bodyBold: 'Matter Bold',
  bodyLight: 'Matter Light',
}

const space = {
  1: '4px',
  2: '8px',
  3: '16px',
  4: '24px',
  5: '32px',
  6: '48px',
  7: '64px',
}

export const themeExtras = {
  components,
  fontConfig,
  fonts,
  fontSizes,
  space,
  extraColors: legacyColors,
}

// Type-only anchor for NativeBase's ICustomTheme augmentation: the interface
// needs statically known members, which requires extendTheme's generic to see
// a literal argument. Never called at runtime — the mounted theme is built by
// UIProvider from `appTheme` + `themeExtras`, the same inputs.
const buildCustomThemeType = () =>
  extendTheme({
    components,
    colors,
    config: { initialColorMode: 'light' },
    fontConfig,
    fonts,
    fontSizes,
    space,
  })

// 2. Get the type of the CustomTheme
type CustomThemeType = ReturnType<typeof buildCustomThemeType>

// 3. Extend the internal NativeBase Theme
declare module 'native-base' {
  interface ICustomTheme extends CustomThemeType {}
}
